import React, { useState, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import HeaderSection from "../../components/header-section"
import Sidebar from "../../features/sidebar/components/sidebar"
import {
  useGetTestSessionDetailsQuery,
  useGetTestSessionRunsQuery,
  useEndTestSessionMutation,
  useDeleteTestSessionMutation,
  TestSessionRunWithDetails,
} from "../../app/services/meseeknaApiV2"
import { MdScience, MdDelete, MdStop, MdArrowBack, MdCheckCircle, MdPlayArrow } from "react-icons/md"
import { FiClock, FiUser, FiDownload } from "react-icons/fi"

// Icon wrapper components for TypeScript compatibility
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const TestIcon = (props: { size?: number; className?: string }): JSX.Element => <MdScience {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const DeleteIcon = (props: { size?: number; className?: string }): JSX.Element => <MdDelete {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const StopIcon = (props: { size?: number; className?: string }): JSX.Element => <MdStop {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const BackIcon = (props: { size?: number; className?: string }): JSX.Element => <MdArrowBack {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CheckIcon = (props: { size?: number; className?: string }): JSX.Element => <MdCheckCircle {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const PlayIcon = (props: { size?: number; className?: string }): JSX.Element => <MdPlayArrow {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ClockIcon = (props: { size?: number; className?: string }): JSX.Element => <FiClock {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const UserIcon = (props: { size?: number; className?: string }): JSX.Element => <FiUser {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const DownloadIcon = (props: { size?: number; className?: string }): JSX.Element => <FiDownload {...props} />

const TestSessionReviewPage: React.FC = () => {
  const { testSessionId } = useParams<{ testSessionId: string }>()
  const navigate = useNavigate()

  const { data: sessionData, isLoading: isLoadingSession, refetch: refetchSession } = useGetTestSessionDetailsQuery(testSessionId!, {
    skip: !testSessionId,
  })
  const { data: runsData, isLoading: isLoadingRuns } = useGetTestSessionRunsQuery(testSessionId!, {
    skip: !testSessionId,
  })

  const [endTestSession, { isLoading: isEnding }] = useEndTestSessionMutation()
  const [deleteTestSession, { isLoading: isDeleting }] = useDeleteTestSessionMutation()

  const [selectedRun, setSelectedRun] = useState<TestSessionRunWithDetails | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const session = sessionData?.data
  const runs = runsData?.data || []

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "N/A"
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}m ${secs}s`
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Pending</span>
      case "active":
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Active</span>
      case "completed":
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">Completed</span>
      case "expired":
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">Expired</span>
      default:
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">{status}</span>
    }
  }

  const handleEndSession = useCallback(async () => {
    if (!testSessionId) return
    try {
      await endTestSession(testSessionId).unwrap()
      refetchSession()
    } catch (error) {
      console.error("Failed to end session:", error)
    }
  }, [testSessionId, endTestSession, refetchSession])

  const handleDeleteSession = useCallback(async () => {
    if (!testSessionId) return
    try {
      await deleteTestSession(testSessionId).unwrap()
      navigate("/dashboard/super-admin/pending-approvals")
    } catch (error) {
      console.error("Failed to delete session:", error)
    }
  }, [testSessionId, deleteTestSession, navigate])

  const handleExportRun = useCallback((run: TestSessionRunWithDetails) => {
    const exportData = {
      runId: run.id,
      runNumber: run.runNumber,
      participant: run.participant,
      startedAt: run.startedAt,
      completedAt: run.completedAt,
      elapsedSeconds: run.elapsedSeconds,
      finalResultData: run.finalResultData,
    }

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `test-run-${run.runNumber}-${run.participant.email.split("@")[0]}.json`
    a.click()
    URL.revokeObjectURL(url)
  }, [])

  if (isLoadingSession) {
    return (
      <div className="flex bg-[#F9FAFB] min-h-screen w-full overflow-hidden">
        <Sidebar />
        <div className="flex flex-col flex-1 overflow-hidden">
          <HeaderSection />
          <main className="flex-1 overflow-y-auto p-6">
            <div className="max-w-7xl mx-auto">
              <div className="animate-pulse">
                <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="h-32 bg-gray-200 rounded"></div>
                  <div className="h-32 bg-gray-200 rounded"></div>
                  <div className="h-32 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    )
  }

  if (!session) {
    return (
      <div className="flex bg-[#F9FAFB] min-h-screen w-full overflow-hidden">
        <Sidebar />
        <div className="flex flex-col flex-1 overflow-hidden">
          <HeaderSection />
          <main className="flex-1 overflow-y-auto p-6">
            <div className="max-w-7xl mx-auto text-center py-12">
              <TestIcon size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Test Session Not Found</h3>
              <p className="text-gray-500 mb-4">The requested test session does not exist.</p>
              <button
                onClick={() => navigate("/dashboard/super-admin/pending-approvals")}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Back to Pending Approvals
              </button>
            </div>
          </main>
        </div>
      </div>
    )
  }

  return (
    <div className="flex bg-[#F9FAFB] min-h-screen w-full overflow-hidden">
      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <HeaderSection />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Back Button */}
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
            >
              <BackIcon size={20} />
              Back
            </button>

            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <TestIcon size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">{session.name}</h1>
                    <p className="text-gray-500">Test Session Review</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(session.status)}
                </div>
              </div>
            </div>

            {/* Session Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center gap-2 text-gray-500 mb-1">
                  <UserIcon size={16} />
                  <span className="text-sm">Participants</span>
                </div>
                <p className="text-2xl font-semibold text-gray-900">{session.participants.length}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center gap-2 text-gray-500 mb-1">
                  <PlayIcon size={16} />
                  <span className="text-sm">Test Runs</span>
                </div>
                <p className="text-2xl font-semibold text-gray-900">{runs.length}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center gap-2 text-gray-500 mb-1">
                  <CheckIcon size={16} />
                  <span className="text-sm">Completed</span>
                </div>
                <p className="text-2xl font-semibold text-gray-900">
                  {runs.filter(r => r.completedAt).length}
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center gap-2 text-gray-500 mb-1">
                  <ClockIcon size={16} />
                  <span className="text-sm">Expires</span>
                </div>
                <p className="text-sm font-medium text-gray-900">{formatDate(session.expiresAt)}</p>
              </div>
            </div>

            {/* Session Actions */}
            {session.status === "active" && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Session Actions</h3>
                <div className="flex gap-3">
                  <button
                    onClick={handleEndSession}
                    disabled={isEnding}
                    className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
                  >
                    <StopIcon size={18} />
                    {isEnding ? "Ending..." : "End Session"}
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={isDeleting}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    <DeleteIcon size={18} />
                    Delete Session
                  </button>
                </div>
              </div>
            )}

            {/* Simulation Info */}
            {session.v2Simulation && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Simulation</h3>
                <p className="font-medium text-gray-900">{session.v2Simulation.name}</p>
                {session.v2Simulation.description && (
                  <p className="text-sm text-gray-500 mt-1">{session.v2Simulation.description}</p>
                )}
              </div>
            )}

            {/* Test Runs Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Test Runs</h2>
              </div>

              {isLoadingRuns ? (
                <div className="p-6 text-center text-gray-500">Loading runs...</div>
              ) : runs.length === 0 ? (
                <div className="p-12 text-center">
                  <PlayIcon size={48} className="mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Test Runs Yet</h3>
                  <p className="text-gray-500">Test runs will appear here when participants start testing.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Run #</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Participant</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Started</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completed</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {runs.map((run) => (
                        <tr key={run.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            #{run.runNumber}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {run.participant.firstName && run.participant.lastName
                                ? `${run.participant.firstName} ${run.participant.lastName}`
                                : run.participant.email}
                            </div>
                            <div className="text-xs text-gray-500">{run.participant.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(run.startedAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {run.completedAt ? formatDate(run.completedAt) : "-"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDuration(run.elapsedSeconds)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {run.completedAt ? (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                Completed
                              </span>
                            ) : (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                                In Progress
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex gap-2">
                              <button
                                onClick={() => setSelectedRun(run)}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                View
                              </button>
                              {run.finalResultData && (
                                <button
                                  onClick={() => handleExportRun(run)}
                                  className="text-gray-600 hover:text-gray-800"
                                  title="Export run data"
                                >
                                  <DownloadIcon size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Run Details Modal */}
      {selectedRun && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Run #{selectedRun.runNumber} Details
              </h2>
              <button
                onClick={() => setSelectedRun(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                &times;
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-500">Participant</p>
                  <p className="font-medium">{selectedRun.participant.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Duration</p>
                  <p className="font-medium">{formatDuration(selectedRun.elapsedSeconds)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Started</p>
                  <p className="font-medium">{formatDate(selectedRun.startedAt)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Completed</p>
                  <p className="font-medium">{selectedRun.completedAt ? formatDate(selectedRun.completedAt) : "In Progress"}</p>
                </div>
              </div>

              {selectedRun.finalResultData && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Results Data</p>
                  <pre className="bg-gray-50 p-4 rounded-lg text-xs overflow-x-auto">
                    {JSON.stringify(selectedRun.finalResultData, null, 2)}
                  </pre>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setSelectedRun(null)}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Delete Test Session?</h2>
            <p className="text-gray-600 mb-6">
              This will permanently delete the test session and all associated test runs. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteSession}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TestSessionReviewPage
